import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { usePortal } from "../../../../context/PortalsContext";
import { useToast } from "../../../../context/ToastContext";
import { usePageMetadata } from "../../../../context/PageMetadataContext";
import { getHomeAssignments } from "../../../../services/api/apiClient";
import {
  GetHomeAssignmentsParams,
  HomeAssignment,
} from "../../../../services/api/types/general/homeAssignment";
import { MyAssignments } from "../MyAssignments/MyAssignments";
import { extractErrorMessage } from "../../../../utils/errorUtils";


export const AssignmentsView: React.FC = () => {
  const { auth } = useAuth();
  const { portals } = usePortal();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as {
    rootAssignments?: HomeAssignment[];
    clickedAssignment?: HomeAssignment;
    homeSearch?: string;
    autoOpenAssignmentUid?: string;
  } | null;

  const [homeSearch, setHomeSearch] = useState(
    () => locationState?.homeSearch || location.search || "",
  );

  const [assignments, setAssignments] = useState<HomeAssignment[]>([]);
  const [allAssignments, setAllAssignments] = useState<HomeAssignment[]>([]);
  const [assignmentCache, setAssignmentCache] = useState<
    Map<string, HomeAssignment[]>
  >(new Map());
  const [breadcrumbData, setBreadcrumbData] = useState<
    Map<string, HomeAssignment>
  >(new Map());

  // Parse the path to get assignment IDs
  const assignmentIds = useMemo(() => {
    const pathSegments = location.pathname
      .replace("/home/assignments", "")
      .split("/")
      .filter(Boolean);
    return pathSegments;
  }, [location.pathname]);

  const joinedAssignmentIds = assignmentIds.join("/")

  const buildHomeSearch = (filters: GetHomeAssignmentsParams) => {
    const originalHomeParams = new URLSearchParams(homeSearch);
    const nextHomeParams = new URLSearchParams();

    const rootPortalGroupId = originalHomeParams.get("portalGroupId");
    if (rootPortalGroupId) {
      nextHomeParams.set("portalGroupId", rootPortalGroupId);
    }

    if (filters.type) {
      nextHomeParams.set("type", filters.type);
    }

    if (filters.importance) {
      nextHomeParams.set("importance", filters.importance);
    }

    if (filters.progressStatus) {
      nextHomeParams.set("progressStatus", filters.progressStatus);
    }

    return nextHomeParams.toString() ? `?${nextHomeParams.toString()}` : "";
  };

  const getFiltersFromURL = (): GetHomeAssignmentsParams => {
    const params = new URLSearchParams(location.search);

    return {
      groupLevel: assignmentIds.length,
      ...(params.get("portalGroupId") && {
        portalGroupId: params.get("portalGroupId") || undefined,
      }),
      ...(params.get("type") && {
        type: params.get("type") || undefined,
      }),
      ...(params.get("importance") && {
        importance: params.get("importance") || undefined,
      }),
      ...(params.get("progressStatus") && {
        progressStatus: params.get("progressStatus") || undefined,
      }),
    };
  };

  // When navigating from Home into a group, persist the current filters so they apply inside the group
  const [filterParams, setFilterParams] = useState<GetHomeAssignmentsParams>(
    () => getFiltersFromURL(),
  );

  const updateFiltersInURL = (filters: GetHomeAssignmentsParams) => {
    const params = new URLSearchParams(location.search);

    if (filters.portalGroupId) {
      params.set("portalGroupId", filters.portalGroupId);
    } else {
      params.delete("portalGroupId");
    }

    if (filters.type) {
      params.set("type", filters.type);
    } else {
      params.delete("type");
    }

    if (filters.importance) {
      params.set("importance", filters.importance);
    } else {
      params.delete("importance");
    }

    if (filters.progressStatus) {
      params.set("progressStatus", filters.progressStatus);
    } else {
      params.delete("progressStatus");
    }

    const nextSearch = params.toString() ? `?${params.toString()}` : "";
    const nextHomeSearch = buildHomeSearch(filters);

    setHomeSearch(nextHomeSearch);

    navigate(`${location.pathname}${nextSearch}`, {
      replace: true,
      state: {
        ...locationState,
        homeSearch: nextHomeSearch,
      },
    });
  };

  // Build cache key from group context + filter params so filtered results are cached per combination
  const getCacheKey = (
    portalGroupId: string | undefined,
    filters: GetHomeAssignmentsParams,
  ) => {
    const base = portalGroupId || "root";
    const filterPart = [
      filters.portalGroupId,
      filters.type,
      filters.importance,
      filters.progressStatus,
    ]
      .filter(Boolean)
      .join("|");
    return filterPart ? `${base}-${filterPart}` : base;
  };

  // Fetch assignments for a specific group level with optional filters
  const fetchAssignments = async (
    groupLevel: number,
    portalGroupId?: string,
    filters: GetHomeAssignmentsParams = { groupLevel: 0 },
  ) => {
    if (!auth.user || !portals.selectedPortal) return;

    try {
      // The portalGroupId from URL represents the parent group context
      // The filters.portalGroupId is for client-side filtering of visible groups
      const params: GetHomeAssignmentsParams = {
        groupLevel,
        ...(portalGroupId && { portalGroupId }),
        ...(filters.type && { type: filters.type }),
        ...(filters.importance && { importance: filters.importance }),
        ...(filters.progressStatus && {
          progressStatus: filters.progressStatus,
        }),
      };

      const cacheKey = getCacheKey(portalGroupId, filters);
      const cachedData = assignmentCache.get(cacheKey);

      if (cachedData) {
        // Store all assignments (before group filter) for filter options
        setAllAssignments(cachedData);
        // Apply client-side group filtering based on selected groups in filter
        const filteredData = applyClientSideGroupFilter(
          cachedData,
          filters.portalGroupId,
        );
        setAssignments(filteredData);
        return;
      }

      const data = await getHomeAssignments(
        auth.user.UserPortalUid,
        portals.selectedPortal.portalUid,
        params,
      );

      setAssignmentCache((prev) => {
        const newCache = new Map(prev);
        newCache.set(cacheKey, data);
        return newCache;
      });

      // Store all assignments (before group filter) for filter options
      setAllAssignments(data);
      // Apply client-side group filtering based on selected groups in filter
      const filteredData = applyClientSideGroupFilter(
        data,
        filters.portalGroupId,
      );
      setAssignments(filteredData);
    } catch (error: any) {
      console.log("Error:", error);
      showToast(
        extractErrorMessage(error, "Failed to fetch assignments"),
        "error",
      );
    }
  };

  // Apply client-side filtering by group IDs
  // The groups filter now shows only currently visible groups, so we filter client-side
  const applyClientSideGroupFilter = (
    data: HomeAssignment[],
    filterGroupIds: string | undefined,
  ): HomeAssignment[] => {
    if (!filterGroupIds) {
      return data;
    }

    const selectedGroupIds = filterGroupIds.split("|");
    return data.filter((assignment) =>
      selectedGroupIds.includes(assignment.assignmentId),
    );
  };

  const handleFiltersChange = (filters: GetHomeAssignmentsParams) => {
    setFilterParams(filters);
    updateFiltersInURL(filters);

    const groupLevel = assignmentIds.length;
    const portalGroupId =
      assignmentIds.length > 0
        ? assignmentIds[assignmentIds.length - 1]
        : undefined;
    fetchAssignments(groupLevel, portalGroupId, filters);
  };

  // Build breadcrumbs from the path
  const buildBreadcrumbs = useMemo(() => {
    const breadcrumbs = [{ label: "Home", path: `/home${homeSearch}` }];

    // Build breadcrumbs from assignment IDs in the path
    const pathSegments = assignmentIds;
    for (let i = 0; i < pathSegments.length; i++) {
      const assignmentId = pathSegments[i];
      const assignment = breadcrumbData.get(assignmentId);
      if (!assignment) continue;

      breadcrumbs.push({
        label: assignment.title,
        path: `/home/assignments/${assignmentIds.slice(0, i + 1).join("/")}${location.search}`,
      });
    }

    return breadcrumbs;
  }, [assignmentIds, breadcrumbData, homeSearch, location.search]);

  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    setFilterParams(urlFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Initialize cache with root assignments from location state if available
  useEffect(() => {
    if (locationState?.rootAssignments) {
      // Initialize cache with root assignments if we have them from location state
      setAssignmentCache((prev) => {
        if (!prev.has("root")) {
          const newCache = new Map(prev);
          newCache.set("root", locationState.rootAssignments!);
          return newCache;
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Fetch breadcrumb data for all parent assignments
  useEffect(() => {
    const fetchBreadcrumbData = async () => {
      if (!auth.user || !portals.selectedPortal || assignmentIds.length === 0) {
        setBreadcrumbData(new Map());
        return;
      }

      const newBreadcrumbData = new Map<string, HomeAssignment>();

      // Fetch data for each level to get assignment titles
      // We can use cached data if available
      for (let i = 0; i < assignmentIds.length; i++) {
        const assignmentId = assignmentIds[i];
        if (newBreadcrumbData.has(assignmentId)) continue;

        // Check if we have the assignment from location state (for first level)
        if (
          i === 0 &&
          locationState?.clickedAssignment &&
          assignmentId === locationState.clickedAssignment.assignmentId
        ) {
          newBreadcrumbData.set(assignmentId, locationState.clickedAssignment);
          continue;
        }

        // Check if we have cached data for this level
        const parentId = i > 0 ? assignmentIds[i - 1] : undefined;
        const cacheKey = parentId || "root";
        let levelData = assignmentCache.get(cacheKey);

        // For root level, also check location state if cache doesn't have it yet
        if (i === 0 && !levelData && locationState?.rootAssignments) {
          levelData = locationState.rootAssignments;
          // Cache it for future use
          setAssignmentCache((prev) => {
            if (!prev.has("root")) {
              const newCache = new Map(prev);
              newCache.set("root", locationState.rootAssignments!);
              return newCache;
            }
            return prev;
          });
        }

        if (levelData) {
          const assignment = levelData.find(
            (a) => a.assignmentId === assignmentId,
          );
          if (assignment) {
            newBreadcrumbData.set(assignmentId, assignment);
            continue;
          }
        }

        // If not cached and not in location state, fetch it
        try {
          const params: GetHomeAssignmentsParams = {
            groupLevel: i,
            ...(i > 0 && { portalGroupId: assignmentIds[i - 1] }),
          };

          const data = await getHomeAssignments(
            auth.user.UserPortalUid,
            portals.selectedPortal.portalUid,
            params,
          );

          // Cache the data
          setAssignmentCache((prev) => {
            const newCache = new Map(prev);
            newCache.set(cacheKey, data);
            return newCache;
          });

          const assignment = data.find((a) => a.assignmentId === assignmentId);
          if (assignment) {
            newBreadcrumbData.set(assignmentId, assignment);
          }
        } catch (error) {
          console.error(`Failed to fetch breadcrumb data for ${assignmentId}`);
        }
      }

      setBreadcrumbData(newBreadcrumbData);
    };

    fetchBreadcrumbData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentIds.join("/"), auth.user, portals.selectedPortal]);

  // Fetch assignments when path changes (initial load or navigation)
  // Filter changes are handled directly by handleFiltersChange
  useEffect(() => {
    if (!auth.user || !portals.selectedPortal) return;

    const groupLevel = assignmentIds.length;
    const portalGroupId =
      assignmentIds.length > 0
        ? assignmentIds[assignmentIds.length - 1]
        : undefined;

    const urlFilters = getFiltersFromURL();
    setFilterParams(urlFilters);
    fetchAssignments(groupLevel, portalGroupId, urlFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    joinedAssignmentIds,
    auth.user,
    portals.selectedPortal,
    location.search,
  ]);

  // Set breadcrumbs in page metadata
  usePageMetadata(
    {
      title: "",
      breadcrumbs: buildBreadcrumbs.map((crumb, index) => ({
        label: crumb.label,
        path: index === buildBreadcrumbs.length - 1 ? undefined : crumb.path,
        onClick:
          index === 0 && crumb.path ? () => navigate(crumb.path) : undefined,
      })),
    },
    [buildBreadcrumbs, navigate, location.search],
  );

  // Handle card click - navigate to nested route
  const handleCardClick = (assignment: HomeAssignment) => {
    if (assignment.isLocked) {
      // Locked assignments are handled inside MyAssignments via the modal
      return;
    }

    if (assignment.isDirectAssignment) {
      localStorage.setItem(
        `assignment-mode:${assignment.assignmentId}`,
        assignment.mode,
      );
      navigate(`/assignment/${assignment.assignmentId}`, {
        state: {
          parentBreadcrumbs: buildBreadcrumbs,
          parentPath:
            assignmentIds.length > 0
              ? `/home/assignments/${assignmentIds.join("/")}`
              : "/home",
          mode: assignment.mode,
        },
      });
    } else {
      const params = new URLSearchParams(location.search);
      params.delete("portalGroupId");

      const query = params.toString();
      const nextSearch = query ? `?${query}` : "";

      const newPath = `/home/assignments/${[
        ...assignmentIds,
        assignment.assignmentId,
      ].join("/")}${nextSearch}`;

      navigate(newPath, {
        state: {
          ...locationState,
          homeSearch,
        },
      });
    }
  };

  // groupLevel is derived from the number of assignment IDs in the path
  const groupLevel = assignmentIds.length;

  return (
    <MyAssignments
      assignments={assignments}
      onCardClick={handleCardClick}
      onFiltersChange={handleFiltersChange}
      groupLevel={groupLevel}
      activeFilters={filterParams}
      allAssignments={allAssignments}
      autoOpenAssignmentUid={locationState?.autoOpenAssignmentUid}
    />
  );
};
